/*
 * Copyright (c) 2021 LucidResolutions LLC. All rights reserved.
 */

import React, {useEffect, useState} from "react";
import {
    Badge,
    Button,
    Card,
    Col,
    Input,
    Layout,
    notification,
    Row,
    Skeleton,
    Space,
    Statistic,
    Table,
    Tooltip,
    Typography
} from "antd";
import {ClockCircleOutlined, ExperimentOutlined} from '@ant-design/icons';
import {DefenderService} from "../../service/defender.service";
import UserService from "../../service/user.service";
import {Link} from "react-router-dom";
import {IfTruthy} from "../../component/if.equal.component";

const svc = new DefenderService()
const userSVC = new UserService()

const { TextArea } = Input;

const DashboardPage = (props: any) =>{
    const [pageLoading, setPageLoading] = useState(true)
    const [availableUTXOs, setAvailableUTXOs] = useState(0)
    const [usedUTXOs, setUsedUTXOs] = useState(0)
    const [val, setVal] = useState("")
    const [isLoading, setLoading] = useState(false)
    const [transactions, setTransactions] = useState([])
    const [bumpVal, setBumpVal] = useState('')

    const updateUTXOS = ()=>{
        return userSVC.info().then((info)=>{
            setUsedUTXOs(info.utxos_utilized)
            setAvailableUTXOs(info.utxos)
        })
    }
    useEffect(() => {
        Promise.all([
            updateUTXOS(),
            svc.listTransactions().then((res)=>{
                setTransactions(res.results)
            })
        ]).then(()=>{
            setPageLoading(false)
        })
    }, []);

    const defend = ()=>{
        setLoading(true)
        let info: any = (window as any).transactionInfo?.(val) || {"error": "ruh oh"}
        if(info.error){
            setLoading(false)
            notification.error({
                message: 'Invalid Transaction Detected',
                description:
                    "You've entered an invalid transaction",
            });
            return
        }
        let tx: any = info.transaction;
        if(tx.totalInputs > (availableUTXOs - usedUTXOs)){
            setLoading(false)
            notification.error({
                message: 'Too Many UTXOs',
                description:
                    `The transaction you've entered references ${tx.totalInputs} UTXOs. You can only defend up to ${availableUTXOs} UTXOs`,
            });
            return;
        }

        svc.mainNet(val).then(()=>{
            notification.success({
                message: 'Transaction Defended',
                description:
                    "Your transaction is now defended",
            });
            setVal('')
        }).catch((e)=>{
            console.error(e)
            notification.error({
                message: 'Something went wrong',
                description:
                    "Verify that transaction is accurate and try again",
            });
        })
            .finally(()=>{
                setLoading(false)
                svc.listTransactions().then((res)=>{
                    setTransactions(res.results)
                }).finally(()=>{
                    updateUTXOS()
                })
            })
    }

    const bumpTransaction = (originalTx: any, hex: any)=>{
        setLoading(true)
        let info: any = (window as any).transactionInfo?.(hex) || {"error": "ruh oh"}
        if(info.error){
            setLoading(false)
            notification.error({
                message: 'Invalid Transaction Detected',
                description:
                    "You've entered an invalid transaction",
            });
            return
        }
        let tx: any = info.transaction;
        if(tx.totalInputs != originalTx.utxos){
            setLoading(false)
            notification.error({
                message: 'Input Mismatch',
                description:
                    `The transaction you entered has a different amount of inputs than the original transaction`,
            });
            return;
        }

        svc.bumpTransaction(originalTx, hex).catch(()=>{
            notification.error({
                message: 'Something went wrong',
                description:
                    "Verify that transaction is accurate and try again",
            });
        }).finally(()=>{
            setLoading(false)
            svc.listTransactions().then((res)=>{
                setTransactions(res.results)
            }).finally(()=>{
                updateUTXOS()
            })
        })
    }

    const removeTransaction = (id: any, isTestNet: any) =>{
        svc.deleteTransaction(id, isTestNet).then(()=>{
            setTransactions(transactions.filter((f: any) => !(f.id == id && f.is_test_net == isTestNet)))
            updateUTXOS()
        })
    }


    const columns = [
        {
            title: 'Transaction',
            dataIndex: 'id',
            render: (_: any, record: any) =>{
                let url = "https://mempool.space/";
                if(record.is_test_net){
                    url += "testnet/"
                }
                url += `tx/${record.id}`
               return <>
                   <IfTruthy val={!record.is_test_net}>
                       <img style={{height: 15, marginRight: 5}} src='/img/bitcoin-logo.svg'/>
                   </IfTruthy>
                   <IfTruthy val={record.is_test_net}>
                       <ExperimentOutlined  style={{marginRight: 5}}/>
                   </IfTruthy>
                   <a href={url} target='_blank' rel="noreferrer">{record.id.substr(0, 3)}...</a>
                   <IfTruthy val={record.expires_at}>
                       <Tooltip title={`this transaction is expiring soon`}>
                           <ClockCircleOutlined style={{marginLeft: 5}} />
                       </Tooltip>
                   </IfTruthy>
               </>
            },
        },
        {
            title: 'Monitored UTXOs',
            dataIndex: 'utxos',
            key: 'utxos',
        },
        {
            title: 'Counter Measures',
            dataIndex: 'bump_transactions',
            key: 'bump_transactions',
        },
        {
            title: 'Action',
            dataIndex: 'address',
            render: (_: any, record: any) => (
                <Space size="middle">
                    <Button type={'primary'} danger onClick={()=>{
                        removeTransaction(record.id, record.is_test_net)
                    }}>Delete</Button>
                </Space>
            )
        },
    ];

    // @ts-ignore
    return <Layout key={'main-content'}>
        <Skeleton loading={pageLoading}>

            <Row gutter={16}>
                <Col span={12}>
                    <Card>
                    <Statistic title="Available UTXOs" value={availableUTXOs} />
                    </Card>
                </Col>
                <Col span={12}>
                    <Card>
                    <Statistic title="Monitored UTXOs" value={usedUTXOs} precision={0} />
                    </Card>
                </Col>
            </Row>


            <Row style={{marginTop: 30}}>
            <Col md={16} xs={24} lg={16} style={{marginLeft: "auto", marginRight: "auto"}}>
                <Badge.Ribbon placement={'start'} text="Defender">
                    <Layout.Content style={{background: '#fff', padding: 10, paddingTop: 50}}>
                        <Typography>
                            <Typography.Title>
                                Enter Signed Transaction
                            </Typography.Title>
                            <Typography.Paragraph>
                                Paste a hex-encoded, signed transaction. &nbsp;
                                <Link to={'public-defender'}>Click here to use a testnet transaction</Link>
                            </Typography.Paragraph>

                        </Typography>

                        <TextArea rows={4}
                                  placeholder="Enter hex-encoded transaction here..."
                                  value={val}
                                  onChange={(e) => setVal(e.target.value)}/>
                        <Button loading={isLoading} type="primary" onClick={defend}
                                style={{marginTop: 10, marginBottom: 10}}>
                            Defend
                        </Button>

                        <IfTruthy val={transactions?.length}>
                            <h2>Active Defenders</h2>

                            <Table
                                dataSource={transactions}
                                expandable={{
                                    expandedRowRender: record => <>
                                        <h3>Add a Counter Measure</h3>
                                        <p>You can add up to 10 counter measures with a higher fee than the original transaction</p>
                                        <p>This will help against an active attacker</p>
                                        <p><strong>Note:</strong> Counter measures must have the same inputs as the original transaction listed above</p>


                                        <TextArea rows={4}
                                                  placeholder="Enter hex-encoded transaction here..."
                                                  value={bumpVal}
                                                  onChange={(e) => setBumpVal(e.target.value)}/>
                                        <Button loading={isLoading} type="primary" onClick={()=> bumpTransaction(record, bumpVal)}
                                                style={{marginTop: 10, marginBottom: 10}}>
                                            Add Counter Measure
                                        </Button>

                                    </>,
                                    rowExpandable: ()=> true,

                                }}
                                columns={columns}/>
                        </IfTruthy>


                    </Layout.Content>
                </Badge.Ribbon>
            </Col>
            </Row>

        </Skeleton>
    </Layout>
}

export {DashboardPage}
